import type { GraphOpportunityActionRequest, GraphOpportunityFeedItem } from "@salesko/contracts";
import type { Dictionary } from "../../i18n";

type OpportunityAction = GraphOpportunityActionRequest["action"];

export function opportunityInboxCounts(items: GraphOpportunityFeedItem[]) {
  return items.reduce(
    (counts, item) => {
      if (item.status === "candidate") {
        counts.pending += 1;
      } else if (item.status === "active") {
        counts.saved += 1;
      }
      return counts;
    },
    { pending: 0, saved: 0 }
  );
}

export function opportunityStatusLabel(status: GraphOpportunityFeedItem["status"], t: Dictionary) {
  switch (status) {
    case "candidate":
      return t.signals.status.pending;
    case "active":
      return t.signals.status.saved;
    case "dismissed":
      return t.signals.status.dismissed;
    case "archived":
      return t.signals.status.archived;
  }
}

export function opportunityActionLabel(action: OpportunityAction, item: GraphOpportunityFeedItem, t: Dictionary) {
  switch (action) {
    case "dismiss":
      return t.signals.actions.dismiss;
    case "save":
      return item.status === "active" ? t.signals.actions.saved : t.signals.actions.save;
    case "follow_up":
      return opportunityFollowUpRequestedAt(item) ? t.signals.actions.followUpAdded : t.signals.actions.followUp;
  }
}

export function opportunityActionAlreadyApplied(action: OpportunityAction, item: GraphOpportunityFeedItem) {
  return (action === "save" && item.status === "active") ||
    (action === "follow_up" && opportunityFollowUpRequestedAt(item) !== null);
}

export function opportunityFollowUpRequestedAt(item: GraphOpportunityFeedItem) {
  return stringDataField(item.data, ["followUpRequestedAt"]);
}

export function opportunityEvidenceSnippets(item: GraphOpportunityFeedItem) {
  return stringArrayDataField(item.data, "evidenceSnippets");
}

export function opportunityHasEvidence(item: GraphOpportunityFeedItem) {
  return item.evidenceSpanIds.length > 0 || opportunityEvidenceSnippets(item).length > 0;
}

export function opportunitySourceFrameRecordId(item: GraphOpportunityFeedItem) {
  return stringDataField(item.data, ["sourceFrameRecordId", "frameRecordId"]);
}

export function opportunityReason(item: GraphOpportunityFeedItem) {
  return stringDataField(item.data, ["reason", "why", "rationale", "sourceSummary"]);
}

export function opportunityInsightType(item: GraphOpportunityFeedItem) {
  const value = stringDataField(item.data, ["insightType", "insight_type", "type"]);
  return value ? value.replace(/_/g, " ") : null;
}

export function opportunitySuggestedNextAction(item: GraphOpportunityFeedItem) {
  return stringDataField(item.data, ["suggestedNextAction", "nextAction"]);
}

function stringDataField(data: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function stringArrayDataField(data: Record<string, unknown>, key: string) {
  const value = data[key];
  return Array.isArray(value)
    ? [
        ...new Set(
          value
            .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
            .map((item) => item.trim())
        )
      ]
    : [];
}
